import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Key, Check, HelpCircle, Loader2, Zap, LogOut } from 'lucide-react';

const API_URL = '/api';

const Settings = () => {
  const [gmailEmail, setGmailEmail] = useState('');
  const [gmailPassword, setGmailPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');

  useEffect(() => {
    const savedApiKey = localStorage.getItem('apiKey');
    if (savedApiKey) setApiKey(savedApiKey);
    
    const savedGmail = localStorage.getItem('gmailEmail');
    if (savedGmail) {
      setGmailEmail(savedGmail);
      setIsGmailConnected(true);
    }
    checkGmailStatus();
  }, []);

  const checkGmailStatus = async () => {
    try {
      const res = await axios.get('/api/gmail/status');
      setIsGmailConnected(res.data?.connected === true);
    } catch {
      setIsGmailConnected(!!localStorage.getItem('gmailEmail'));
    }
  };

  const connectGmail = async () => {
    if (!gmailEmail) {
      alert('Please enter your Gmail address!');
      return;
    }

    const password = gmailPassword.replace(/\s/g, '');
    if (!password) {
      alert('Please enter your App Password!');
      return;
    }

    if (password.length !== 16) {
      alert(`App Password must be exactly 16 characters!\n\nCurrent: ${password.length} characters`);
      return;
    }

    setLoading(true);
    setLoadingText('Connecting to Gmail...');

    try {
      const response = await axios.post(`${API_URL}/gmail/connect`, {
        email: gmailEmail,
        password: password,
        sessionId: 'react-session'
      });

      if (response.data.success) {
        setIsGmailConnected(true);
        localStorage.setItem('gmailEmail', gmailEmail);
        alert('✅ Gmail Connected!\n\nYou can now create drafts!');
      }
    } catch (error) {
      alert(`❌ Connection Failed!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail?')) return;
    setLoading(true);
    setLoadingText('Disconnecting...');
    try {
      await axios.post(`${API_URL}/gmail/disconnect`);
      setIsGmailConnected(false);
      localStorage.removeItem('gmailEmail');
      setGmailPassword('');
      alert('Gmail disconnected');
    } catch (e) {
      alert(`Failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveApiKey = () => {
    if (!apiKey) {
      alert('Please enter your API key!');
      return;
    }
    localStorage.setItem('apiKey', apiKey);
    alert('✅ API Key saved successfully!');
  };

  const showPasswordHelp = () => {
    const helpText = `📧 HOW TO GET GMAIL APP PASSWORD:\n\n` +
                     `1️⃣ Go to: myaccount.google.com\n` +
                     `2️⃣ Click "Security"\n` +
                     `3️⃣ Enable "2-Step Verification"\n` +
                     `4️⃣ Search for "App passwords"\n` +
                     `5️⃣ Select "Mail" and "Windows Computer"\n` +
                     `6️⃣ Click "Generate"\n` +
                     `7️⃣ Copy 16-character password\n` +
                     `8️⃣ Paste here (spaces auto-removed)`;
    alert(helpText);
  };

  return (
    <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
          <Loader2 className="h-12 w-12 animate-spin text-white" />
          <p className="mt-4 text-xl font-semibold text-white">{loadingText}</p>
        </div>
      )}

      <Header 
        title="Settings" 
        subtitle="Configure Gmail connection and API settings"
      />

      <div className="p-8 max-w-4xl mx-auto">
        <div className="space-y-6">
          {/* Gmail Connection Card */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Mail className="h-6 w-6 text-purple-600" />
                Gmail Connection
              </CardTitle>
              <CardDescription className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Direct Gmail integration - No browser needed!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="gmail-email">Gmail Address</Label>
                <Input
                  id="gmail-email"
                  type="email"
                  value={gmailEmail}
                  onChange={(e) => setGmailEmail(e.target.value)}
                  placeholder="yourname@gmail.com"
                  disabled={isGmailConnected}
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gmail-password">App Password</Label>
                <Input
                  id="gmail-password"
                  type="password"
                  value={gmailPassword}
                  onChange={(e) => setGmailPassword(e.target.value)}
                  placeholder="16 characters, no spaces"
                  maxLength="16"
                  disabled={isGmailConnected}
                  className="h-11 font-mono"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={showPasswordHelp}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  How to get App Password?
                </Button>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={connectGmail}
                  disabled={isGmailConnected}
                  className="flex-1 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Connect
                </Button>
                <Button
                  onClick={disconnectGmail}
                  disabled={!isGmailConnected}
                  variant="outline"
                  className="flex-1 h-12 border-red-300 text-red-600 hover:bg-red-50"
                >
                  <LogOut className="mr-2 h-5 w-5" />
                  Disconnect
                </Button>
              </div>

              {isGmailConnected && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                  <p className="font-semibold mb-1">✅ Connection Active</p>
                  <p className="text-xs">Connected as: {gmailEmail}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* API Key Card */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Key className="h-6 w-6 text-blue-600" />
                AI API Configuration
              </CardTitle>
              <CardDescription>
                Configure Groq or Gemini API key for AI generation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="api-key">API Key (Groq/Gemini)</Label>
                <Input
                  id="api-key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your API key"
                  className="h-11 font-mono"
                />
                <p className="text-xs text-gray-500">
                  Groq: gsk_... | Gemini: AIza...
                </p>
              </div>

              <Button
                onClick={saveApiKey}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-base"
              >
                <Check className="mr-2 h-5 w-5" />
                Save API Key
              </Button>

              {apiKey && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  <p className="font-semibold mb-1">✅ API Key Configured</p>
                  <p className="text-xs">Type: {apiKey.startsWith('gsk_') ? 'Groq' : 'Gemini'}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-3">📌 Important Notes:</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>Gmail App Password is required (not your regular password)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>2-Step Verification must be enabled on your Gmail account</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>API keys are stored locally in your browser</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>You need both Gmail and API key for full functionality</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;

